/**
 * Metric interface for gradient calculations
 * Contains only the fields needed by gradient utility functions
 * Allows additional properties for compatibility with GameMetrics
 */
export interface GradientMetric {
    code?: string | null;
    expected_avg?: number | null;
    expected_std?: number | null;
    available_for?: string | null;
    [key: string]: any;
}

export function intToOrdinal(number: number) {
    if (10 <= number % 100 && number % 100 <= 20) {
        return number + "th";
    } else {
        const lastDigit = number % 10;
        const suffix = lastDigit === 1 ? "st" : lastDigit === 2 ? "nd" : lastDigit === 3 ? "rd" : "th";
        return number + suffix;
    }
}

// ============================================================================
// GRADIENT UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate statistical gradient class for percentage values using z-score
 * @param value - The value to evaluate
 * @param metric - The GradientMetric object containing expected_avg and expected_std
 * @param games - Optional number of games for statistical significance
 * @returns Gradient class string (gradient-1 to gradient-5)
 */
export function getStatisticalGradientClass(value: any, metric?: GradientMetric, games?: number): string {
    
    if (!metric || !metric.expected_avg || !metric.expected_std) {
        return getDefaultGradientClass(value);
    }
    
    // Determine if this is a percentage metric (winrate, pickrate, banrate, etc.)
    const isPercentageMetric = metric.code === 'winrate' || 
                               metric.code === 'pickrate' || 
                               metric.code === 'banrate' || 
                               metric.code === 'presence' ||
                               metric.code === 'exp_bin_winrate';
    
    // Determine if this is a rating metric (should not use games-based calculation)
    const isRatingMetric = metric.available_for === 'ratings';
    
    // Determine if this is winrate (special handling)
    const isWinrateMetric = metric.code === 'winrate';
    
    if (games && games >= 150 && !isRatingMetric) {
        const standardError = metric.expected_std / Math.sqrt(games);
        const criticalValue = 1.96;
        const lowerBound = metric.expected_avg - (criticalValue * standardError);
        const upperBound = metric.expected_avg + (criticalValue * standardError);
        
        if (value < lowerBound) return 'gradient-1';
        if (value < metric.expected_avg) return 'gradient-2';
        if (value < upperBound) return 'gradient-3';
        
        // Only apply high percentage logic for actual percentage metrics
        if (isPercentageMetric && value >= 0.80) return 'gradient-5';
        
        // For values above upper bound, use z-score to determine gradient
        const zScore = (value - metric.expected_avg) / metric.expected_std;
        if (zScore >= 1.0) return 'gradient-5';
        if (zScore >= 0.5) return 'gradient-4';
        return 'gradient-4'; // Default to gradient-4 for values above upper bound
    }
    
    const zScore = (value - metric.expected_avg) / metric.expected_std;
    
    // Special handling for rating metrics
    if (isRatingMetric) {
        if (zScore >= 2.0) {
            return 'gradient-5';  // Blue: > avg + 2sd
        }
        if (zScore >= 1.0) {
            return 'gradient-4';  // Green: > avg + 1sd
        }
        if (zScore >= 0) {
            return 'gradient-3';  // Orange: > avg
        }
        if (zScore >= -1.0) {
            return 'gradient-2';  // Light orange: > avg - 1sd
        }
        return 'gradient-1';      // Red: <= avg - 1sd
    }
    
    // Special handling for winrate when games < 300 - use default percentage thresholds
    if (isWinrateMetric && (!games || games < 300)) {
        if (value >= 0.8) return 'gradient-5';  // 80%+
        if (value >= 0.6) return 'gradient-4';  // 60-79%
        if (value >= 0.5) return 'gradient-3';  // 50-59%
        if (value >= 0.4) return 'gradient-2';  // 40-49%
        return 'gradient-1';                    // <40%
    }
    
    // Only apply high percentage logic for actual percentage metrics (when games are available)
    if (isPercentageMetric && value >= 0.8) {
        return 'gradient-5';
    }
    
    // Universal z-score thresholds that work for all other metrics
    if (zScore >= 1.0) {
        return 'gradient-5';  // Very high performance (top ~16%)
    }
    if (zScore >= 0.5) {
        return 'gradient-4';  // High performance (top ~31%)
    }
    if (zScore >= 0) {
        return 'gradient-3';    // Average performance (top ~50%)
    }
    if (zScore >= -0.5) {
        return 'gradient-2'; // Below average (bottom ~31%)
    }
    return 'gradient-1';                     // Poor performance (bottom ~16%)
}

/**
 * Calculate statistical gradient class for delta percentage values using z-score
 * @param value - The delta value to evaluate
 * @param metric - The GradientMetric object containing expected_avg and expected_std
 * @param games - Optional number of games for statistical significance
 * @returns Gradient class string (gradient-1 to gradient-4)
 */
export function getStatisticalDeltaGradientClass(value: any, metric?: GradientMetric, games?: number): string {
    if (!metric || !metric.expected_avg || !metric.expected_std) {
        return getDefaultDeltaGradientClass(value);
    }
    
    if (games && games > 0) {
        const standardError = metric.expected_std / Math.sqrt(games);
        const criticalValue = 1.96;
        const lowerBound = metric.expected_avg - (criticalValue * standardError);
        const upperBound = metric.expected_avg + (criticalValue * standardError);
        
        if (value < lowerBound) return 'gradient-1';
        if (value < metric.expected_avg) return 'gradient-2';
        if (value < upperBound) return 'gradient-3';
        return 'gradient-4';
    }
    
    const zScore = (value - metric.expected_avg) / metric.expected_std;
    if (zScore >= 0.75) return 'gradient-4';
    if (zScore >= 0) return 'gradient-3';
    if (zScore >= -0.75) return 'gradient-2';
    return 'gradient-1';
}

/**
 * Calculate statistical gradient class for delta rating values using z-score
 * @param value - The delta rating value to evaluate
 * @param metric - The GradientMetric object containing expected_avg and expected_std
 * @param games - Optional number of games for statistical significance
 * @returns Gradient class string (gradient-1 to gradient-4)
 */
export function getStatisticalDeltaRatingGradientClass(value: any, metric?: GradientMetric, games?: number): string {
    if (!metric || !metric.expected_avg || !metric.expected_std) {
        return getDefaultDeltaRatingGradientClass(value);
    }
    
    if (games && games > 0) {
        const standardError = metric.expected_std / Math.sqrt(games);
        const criticalValue = 1.96;
        const lowerBound = metric.expected_avg - (criticalValue * standardError);
        const upperBound = metric.expected_avg + (criticalValue * standardError);
        
        if (value < lowerBound) return 'gradient-1';
        if (value < metric.expected_avg) return 'gradient-2';
        if (value < upperBound) return 'gradient-3';
        return 'gradient-4';
    }
    
    const zScore = (value - metric.expected_avg) / metric.expected_std;
    if (zScore >= 0.75) return 'gradient-4';
    if (zScore >= 0) return 'gradient-3';
    if (zScore >= -0.75) return 'gradient-2';
    return 'gradient-1';
}

/**
 * Calculate statistical gradient class for rating values using z-score
 * @param value - The rating value to evaluate
 * @param metric - The GradientMetric object containing expected_avg and expected_std
 * @param games - Optional number of games for statistical significance
 * @returns Gradient class string (gradient-1 to gradient-5)
 */
export function getStatisticalRatingGradientClass(value: any, metric?: GradientMetric, games?: number): string {
    if (!metric || !metric.expected_avg || !metric.expected_std) {
        return getDefaultRatingGradientClass(value);
    }
    
    // Determine if this is a rating metric that should use high value logic
    const isRatingMetric = metric.code === 'rel_rate' || 
                          metric.code === 'rating' ||
                          metric.code === 'projected_competitive_rating';
    
    if (games && games > 0) {
        const standardError = metric.expected_std / Math.sqrt(games);
        const criticalValue = 1.96;
        const lowerBound = metric.expected_avg - (criticalValue * standardError);
        const upperBound = metric.expected_avg + (criticalValue * standardError);
        
        if (value < lowerBound) return 'gradient-1';
        if (value < metric.expected_avg) return 'gradient-2';
        if (value < upperBound) return 'gradient-3';
        
        // Only apply high rating logic for actual rating metrics
        if (isRatingMetric && value >= 0.8) return 'gradient-5';
        return 'gradient-4';
    }
    
    const zScore = (value - metric.expected_avg) / metric.expected_std;
    
    // Only apply high rating logic for actual rating metrics
    if (isRatingMetric && value >= 0.8) return 'gradient-5';
    if (zScore >= 0.75) return 'gradient-4';
    if (zScore >= 0) return 'gradient-3';
    if (zScore >= -0.75) return 'gradient-2';
    return 'gradient-1';
}

// ============================================================================
// DEFAULT GRADIENT FUNCTIONS (FALLBACKS)
// ============================================================================

/**
 * Default gradient logic for fallback when no metric data is available
 * @param value - The value to evaluate
 * @returns Gradient class string (gradient-1 to gradient-5)
 */
export function getDefaultGradientClass(value: any): string {
    let val = value;
    if (value === true) val = 1;
    if (value === false) val = 0;
    
    if (val === undefined || val === null) return 'gradient-1';
    
    // For values that are already percentages (0-1 range), use them directly
    // For values that might be raw numbers, use a different scale
    if (val <= 1 && val >= 0) {
        // This is likely a percentage (0-1 range)
        const percentage = val * 100;
        if (percentage >= 80) return 'gradient-5';
        if (percentage >= 60) return 'gradient-4';
        if (percentage >= 50) return 'gradient-3';
        if (percentage >= 40) return 'gradient-2';
        return 'gradient-1';
    } else {
        // This is likely a raw number, use a different scale
        if (val >= 0.8) return 'gradient-5';
        if (val >= 0.6) return 'gradient-4';
        if (val >= 0.5) return 'gradient-3';
        if (val >= 0.3) return 'gradient-2';
        return 'gradient-1';
    }
}

/**
 * Default delta percentage gradient logic for fallback when no metric data is available
 * @param value - The delta value to evaluate
 * @returns Gradient class string (gradient-1 to gradient-4)
 */
export function getDefaultDeltaGradientClass(value: any): string {
    const percentage = value !== undefined && value !== null ? value * 100 : 0;
    if (percentage > 10) return 'gradient-4';
    if (percentage > 1) return 'gradient-3';
    if (percentage > -10) return 'gradient-2';
    return 'gradient-1';
}

/**
 * Default delta rating gradient logic for fallback when no metric data is available
 * @param value - The delta rating value to evaluate
 * @returns Gradient class string (gradient-1 to gradient-4)
 */
export function getDefaultDeltaRatingGradientClass(value: any): string {
    if (value > 0.1) return 'gradient-4';
    if (value > 0) return 'gradient-3';
    if (value > -0.1) return 'gradient-2';
    return 'gradient-1';
}

/**
 * Default rating gradient logic for fallback when no metric data is available
 * @param value - The rating value to evaluate
 * @returns Gradient class string (gradient-1 to gradient-5)
 */
export function getDefaultRatingGradientClass(value: any): string {
    if (value >= 0.8) return 'gradient-5';
    if (value > 0.7) return 'gradient-4';
    if (value > 0.5) return 'gradient-3';
    if (value > 0.3) return 'gradient-2';
    return 'gradient-1';
}
